// frontend/src/components/Sidebar.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Archive, Inbox, FolderOpen, Tag,
  Activity,
  Radio,
  BarChart3,
  Bot,
  Warehouse,
  Boxes,
  BrainCircuit,
  Building2,
  CircleDollarSign,
  ClipboardCheck,
  ClipboardList,
  Library,
  BookOpen,
  CreditCard,
  Database,
  FileText,
  Gauge,
  GitCompare,
  Headphones,
  Landmark,
  Ship,
  LayoutDashboard,
  LineChart,
  MessageCircle,
  PanelLeftClose,
  PanelLeftOpen,
  Plug,
  ReceiptText,
  Route,
  Scale,
  Search,
  Settings,
  Factory,
  FileBarChart,
  ShieldCheck,
  ShoppingCart,
  Sparkles,
  Users,
  WalletCards,
  Workflow,
  ArrowLeft,
  BookMarked,
  History,
  FileSpreadsheet,
  ScanLine,
  Layers,
  Truck,
} from 'lucide-react';
import SidebarGroup from './SidebarGroup';
import SavedViewsSection from './phase2/SavedViewsSection';
import { canUseRoute, getRoutePolicy } from '../routes/routePolicies';

const domainGroups = [
  {
    id: 'intelligence',
    domain: 'AI Intelligence',
    icon: BrainCircuit,
    items: [
      { path: '/ai-chat', icon: Bot, label: 'AI Workspace' },
      { path: '/ai-insights', icon: Sparkles, label: 'Insights', moduleKey: 'ai_insights' },
    ],
  },
  {
    id: 'workspace',
    domain: 'Workspace',
    icon: LayoutDashboard,
    items: [
      { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
      { path: '/document-processing', icon: FileText, label: 'Documents', moduleKey: 'document_processing' },
      { path: '/search', icon: Search, label: 'Search', moduleKey: 'document_processing' },
      { path: '/doc-inbox', icon: Inbox, label: 'Inbox', moduleKey: 'document_processing' },
      { path: '/route-templates', icon: Route, label: 'Routing Templates', moduleKey: 'document_processing' },
      { path: '/validation-rules', icon: ShieldCheck, label: 'Validation Rules', moduleKey: 'document_processing' },
      { path: '/transaction-folders', icon: FolderOpen, label: 'Transaction Folders', moduleKey: 'document_processing' },
      { path: '/cabinet', icon: Archive, label: 'Cabinet', moduleKey: 'document_processing' },
      { path: '/capture-activity', icon: Activity, label: 'Capture Activity', moduleKey: 'document_processing' },
      { path: '/capture-settings', icon: Radio, label: 'Capture Channels', moduleKey: 'document_processing' },
      { path: '/bank-rec', icon: Landmark, label: 'Bank Reconciliation', moduleKey: 'document_processing' },
      { path: '/staged-records', icon: Boxes, label: 'ERP Queue', moduleKey: 'document_processing' },
      { path: '/erp-settings', icon: Plug, label: 'ERP Integration', moduleKey: 'document_processing' },
      { path: '/grn-receiving', icon: ClipboardCheck, label: 'GRN Receiving', moduleKey: 'document_processing' },
      { path: '/master-data', icon: Library, label: 'Master Data', moduleKey: 'document_processing' },
      { path: '/reports', icon: FileBarChart, label: 'Reports', moduleKey: 'document_processing' },
      { path: '/workflows', icon: Workflow, label: 'Workflows', moduleKey: 'workflows' },
      { path: '/erp-data', icon: Database, label: 'ERP Data', moduleKey: 'erp_data' },
      { path: '/inventory', icon: Warehouse, label: 'Inventory Planning', moduleKey: 'erp_data' },
    ],
  },
  {
    id: 'branches',
    domain: 'Branch Operations',
    icon: ClipboardList,
    items: [
      { path: '/count-dashboard', icon: Gauge, label: 'Count Dashboard', moduleKey: 'stock_count' },
      { path: '/stock-count', icon: ClipboardList, label: 'Stock Count', moduleKey: 'stock_count' },
      { path: '/approvals', icon: ShieldCheck, label: 'Approvals', moduleKey: 'stock_count' },
      { path: '/consignment', icon: Boxes, label: 'Consignment', moduleKey: 'consignment' },
      { path: '/sales-reports', icon: FileSpreadsheet, label: 'Sales Reports', moduleKey: 'consignment' },
      { path: '/scanned-sales', icon: ScanLine, label: 'Scanned Sales', moduleKey: 'stock_count' },
      { path: '/batches', icon: Layers, label: 'Batches', moduleKey: 'stock_count' },
      { path: '/transfers', icon: ClipboardCheck, label: 'Transfers', moduleKey: 'transfer_requests' },
      { path: '/transfer-requests', icon: ClipboardList, label: 'Transfer Requests (IPO)', moduleKey: 'transfer_requests' },
      { path: '/transfer-dashboard', icon: LineChart, label: 'Transfer Dashboard', moduleKey: 'transfer_requests' },
      { path: '/routes', icon: Truck, label: 'Delivery Routes', moduleKey: 'transfer_requests' },
      { path: '/stock-card', icon: Warehouse, label: 'Branch Stock', moduleKey: 'stock_count' },
      { path: '/price-lists', icon: Tag, label: 'Price Lists', moduleKey: 'consignment' },
      { path: '/stock-reports', icon: FileBarChart, label: 'Stock Reports', moduleKey: 'consignment' },
      { path: '/sap-variances', icon: Scale, label: 'SAP Variances', moduleKey: 'consignment' },
      { path: '/customer-catalog', icon: BookMarked, label: 'Customer Catalogue', moduleKey: 'consignment' },
      { path: '/employees', icon: Users, label: 'Employees', moduleKey: 'consignment' },
      { path: '/upload-log', icon: History, label: 'Upload Log', moduleKey: 'consignment' },
      { path: '/production', icon: Factory, label: 'Production', moduleKey: 'stock_count' },
    ],
  },
  {
    id: 'exports',
    domain: 'Flower Exports',
    icon: Ship,
    items: [
      { path: '/export-compliance', icon: Ship, label: 'Export Compliance', moduleKey: 'export_compliance' },
      { path: '/warehouse-counts', icon: ClipboardList, label: 'Warehouse Counts', moduleKey: 'export_compliance' },
      { path: '/price-book', icon: BookOpen, label: 'Price Book', moduleKey: 'price_book' },
    ],
  },
  {
    id: 'finance',
    domain: 'Finance',
    icon: WalletCards,
    items: [
      { path: '/finance', icon: WalletCards, label: 'Command Center', moduleKey: 'cashflow' },
      { path: '/ap', icon: ReceiptText, label: 'Accounts Payable', moduleKey: 'ap' },
      { path: '/ap-workbench', icon: ClipboardCheck, label: 'AP Workbench', moduleKey: 'ap' },
      { path: '/procurement-control', icon: ShieldCheck, label: 'Procurement Control', moduleKey: 'ap' },
      { path: '/finance-action-ledger', icon: ShieldCheck, label: 'ERP Action Ledger', moduleKey: 'ap' },
      { path: '/collections', icon: CircleDollarSign, label: 'AR Collections', moduleKey: 'collections' },
      { path: '/ar-collections', icon: FileText, label: 'Draft Review', moduleKey: 'collections' },
      { path: '/collections-workbench', icon: ClipboardCheck, label: 'Collections Workbench', moduleKey: 'collections' },
      { path: '/ar-analytics', icon: BarChart3, label: 'AR Analytics', moduleKey: 'collections' },
      { path: '/ar-disputes', icon: Scale, label: 'Disputes', moduleKey: 'collections' },
      { path: '/purchase-management', icon: ClipboardCheck, label: 'Requisitions & POs', moduleKey: 'rfq' },
      { path: '/rfq', icon: ClipboardList, label: 'RFQ Management', moduleKey: 'rfq' },
      { path: '/supplier-payments', icon: Landmark, label: 'Supplier Payments', moduleKey: 'supplier_payments' },
      { path: '/cashflow', icon: LineChart, label: 'Cash Flow', moduleKey: 'cashflow' },
      { path: '/finance-scenarios', icon: GitCompare, label: 'Scenarios', moduleKey: 'cashflow' },
      { path: '/financial-forecast', icon: BarChart3, label: 'Forecast', moduleKey: 'financial_forecast' },
    ],
  },
  {
    id: 'customers',
    domain: 'Customers & Orders',
    icon: ShoppingCart,
    items: [
      { path: '/customer-orders', icon: ShoppingCart, label: 'Customer Orders' },
      { path: '/customer-support', icon: Headphones, label: 'Support Desk', moduleKey: 'customer_support' },
      { path: '/whatsapp-sales', icon: MessageCircle, label: 'WhatsApp Sales', moduleKey: 'whatsapp_sales' },
    ],
  },
  {
    id: 'operations',
    domain: 'Operations',
    icon: Gauge,
    items: [
      { path: '/ops-dashboard', icon: Gauge, label: 'Ops Dashboard', moduleKey: 'ops_dashboard' },
      { path: '/operations-control', icon: Activity, label: 'Control Center', moduleKey: 'workflows' },
      { path: '/tasks', icon: ClipboardCheck, label: 'Tasks', moduleKey: 'tasks' },
      { path: '/task-escalations', icon: ShieldCheck, label: 'Escalations', moduleKey: 'task_escalations' },
    ],
  },
  {
    id: 'service',
    domain: 'Service',
    icon: Headphones,
    items: [
      { path: '/sales-bot', icon: Bot, label: 'Sales Bot', moduleKey: 'sales_bot' },
      { path: '/communication', icon: MessageCircle, label: 'Communications', moduleKey: 'communication' },
    ],
  },
  {
    id: 'management',
    domain: 'Administration',
    icon: Settings,
    items: [
      { path: '/modules', icon: Boxes, label: 'Module Settings', adminOnly: true },
      { path: '/organisation', icon: Building2, label: 'Organization' },
      { path: '/users', icon: Users, label: 'Users' },
      { path: '/plan', icon: CreditCard, label: 'Plan & Billing' },
      { path: '/tenant-onboarding', icon: ClipboardCheck, label: 'Getting started' },
    ],
  },
  {
    id: 'account',
    domain: 'Account',
    icon: Settings,
    items: [
      { path: '/settings', icon: Settings, label: 'Settings' },
      { path: '/token-usage', icon: Gauge, label: 'Usage' },
    ],
  },
];

function Sidebar({
  isCollapsed,
  onToggle,
  isOpen,
  onNavigate,
  systemHealth,
  enabledModules,
  moduleMeta = {},
}) {
  const location = useLocation();

  const userData = (() => {
    try { return JSON.parse(localStorage.getItem('userData')) || {}; } catch { return {}; }
  })();
  const userRole = userData.role || '';
  const isPlatformAdmin = userData.is_platform_admin === true || userRole === 'super_admin';
  const isAdmin = userRole === 'admin' || isPlatformAdmin;
  const isManager = userRole === 'manager';

  // An office admin who came in on their phone has a desk session held to
  // the filing pages. The menu shows those and nothing else — listing pages
  // they would only be sent away from is clutter, not navigation.
  const deskScope = (() => {
    try { const v = JSON.parse(localStorage.getItem('deskScope') || 'null'); return Array.isArray(v) ? v : null; }
    catch { return null; }
  })();

  const isVisible = (item) => {
    if (deskScope && !deskScope.some((p) => (item.path || '').startsWith(p))) return false;
    if (item.adminOnly && !isAdmin) return false;
    if (!canUseRoute(getRoutePolicy(item.path), userData, enabledModules).allowed) return false;
    if (!item.moduleKey) return true;
    const canActivate = !moduleMeta[item.moduleKey] || moduleMeta[item.moduleKey]?.can_activate !== false;
    if (!canActivate) return false;
    if (!enabledModules) return true;
    const val = enabledModules[`module_${item.moduleKey}`];
    return val !== false;
  };

  const visibleDomains = useMemo(() => {
    if (isManager && !isAdmin) {
      return domainGroups.filter((domain) => domain.id !== 'management');
    }
    return domainGroups;
  }, [isAdmin, isManager]);

  const pathIsInItem = (item) => {
    if (item.path) {
      const [pathname] = item.path.split('#');
      if (location.pathname === pathname) return true;
    }
    return item.children?.some(pathIsInItem) ?? false;
  };

  const groupHasActivePath = (group) => group.items.some(pathIsInItem);

  const defaultExpandedDomains = () => visibleDomains.reduce((acc, domain) => {
    acc[domain.id] = !isCollapsed && (domain.id === 'intelligence' || groupHasActivePath(domain));
    return acc;
  }, {});

  const [expandedDomains, setExpandedDomains] = useState(() => {
    try {
      const saved = localStorage.getItem('sidebar-expanded-domains');
      return saved ? { ...defaultExpandedDomains(), ...JSON.parse(saved) } : defaultExpandedDomains();
    } catch {
      return defaultExpandedDomains();
    }
  });

  useEffect(() => {
    setExpandedDomains((prev) => {
      const next = { ...prev };
      visibleDomains.forEach((domain) => {
        if (next[domain.id] === undefined) {
          next[domain.id] = !isCollapsed && (domain.id === 'workspace' || groupHasActivePath(domain));
        }
        if (groupHasActivePath(domain) && !isCollapsed) {
          next[domain.id] = true;
        }
      });
      return next;
    });
  }, [isCollapsed, location.pathname, visibleDomains]);

  useEffect(() => {
    localStorage.setItem('sidebar-expanded-domains', JSON.stringify(expandedDomains));
  }, [expandedDomains]);

  const toggleDomain = (domainId) => {
    setExpandedDomains((prev) => ({
      ...prev,
      [domainId]: !prev[domainId],
    }));
  };

  return (
    <aside className={`sidebar ${isCollapsed ? 'collapsed' : ''} ${isOpen ? 'open' : ''}`}>
      <div className="sidebar-header">
        <div className="sidebar-logo">
          <span className="logo-icon" aria-hidden="true">
            <Boxes size={22} strokeWidth={1.8} />
          </span>
          {!isCollapsed && <span className="logo-text">Acuon</span>}
        </div>
        <button
          className="sidebar-toggle"
          onClick={onToggle}
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          type="button"
        >
          {isCollapsed ? <PanelLeftOpen size={17} /> : <PanelLeftClose size={17} />}
        </button>
      </div>

      <nav className="sidebar-nav" aria-label="Primary navigation">
        {deskScope && (
          <a href="/branch" onClick={onNavigate}
            className="flex items-center gap-2 mx-2 mb-2 px-3 py-2 rounded-lg text-sm font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100">
            <ArrowLeft size={16} className="shrink-0" />
            {!isCollapsed && <span>Back to the portal</span>}
          </a>
        )}
        {visibleDomains.map((group) => {
          const visibleItems = group.items.filter(isVisible);
          if (visibleItems.length === 0) return null;
          return (
            <React.Fragment key={group.id}>
              <SidebarGroup
                domain={group.domain}
                icon={group.icon}
                isCollapsed={isCollapsed}
                expanded={expandedDomains[group.id] ?? false}
                onToggle={() => toggleDomain(group.id)}
                items={visibleItems}
                isVisible={isVisible}
                location={location}
                onNavigate={onNavigate}
              />
              {/* M11 saved views — below the Workspace group, gated like /search */}
              {group.id === 'workspace' && !deskScope
                && isVisible({ path: '/search', moduleKey: 'document_processing' }) && (
                  <SavedViewsSection isCollapsed={isCollapsed} onNavigate={onNavigate} />
              )}
            </React.Fragment>
          );
        })}
      </nav>

      <div className="sidebar-footer">
        <div className="connection-status">
          <span className={`ws-indicator ${systemHealth?.status === 'healthy' ? 'connected' : 'disconnected'}`} />
          {!isCollapsed && (
            <span className="ws-label">
              {systemHealth?.status === 'healthy' ? 'Connected' : 'Offline'}
            </span>
          )}
        </div>
        {!isCollapsed && <div className="version-info">v1.0.1</div>}
      </div>
    </aside>
  );
}

export default Sidebar;
